import { Button, Combobox, FileModel, Loading, Modal, Upload } from "@appkit4/react-components";
import { SelectValue } from "@appkit4/react-components/esm/combobox/Combobox";
import { FC, useState } from "react";
import { useParams } from "react-router-dom";
import { checkFormValues } from "services/common";
import { uploadData } from "services/api-actions";
import { FormError, ValidationError } from "components/common/helpers";
import { RuleBag, SapSystem } from "routes/analysis/types/analysis";

interface AnalysisFormProps {
  systems: SapSystem[];
  ruleBags: RuleBag[];
  cancel: () => void;
  confirmed: () => void;
  clients: {
    name: string;
    clientId: string;
  }[];
}

type AnalysisFormType = {
  system?: SelectValue;
  ruleset?: SelectValue;
  file?: FileModel;
}

const AnalysisForm: FC<AnalysisFormProps> = ({ cancel, ruleBags, systems, clients, confirmed }) => {
  const { clientId } = useParams();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisFormType>();
  const [errors, setErrors] = useState<(keyof AnalysisFormType)[] | undefined>();
  const [ruleSets] = useState<any[]>(getRuleSets());
  const [progress, setProgress] = useState<number>(0);

  function getRuleSets() {
    let data = [];
    for (let client of clients) {
      data.push({
        label: client.name,
        type: "group",
        children: ruleBags.filter(f => f.clientId === client.clientId).map(m => ({ value: m.bagId, label: m.name, description: m.description }))
      });
    }
    return data;
  }

  const handleSubmit = async () => {
    if (!clientId) return false;
    if (!validateForm()) return false;
    if (!analysis?.file || !analysis.ruleset || !analysis.system) return false;
    setLoading(true);
    let payload = new FormData();
    payload.append("file", analysis.file.originFile as Blob, analysis?.file.name);
    payload.append("systemId", analysis.system.toString());
    payload.append("bagId", analysis.ruleset.toString());
    payload.append("clientId", clientId);
    let request = await uploadData(`client/${clientId}/analysis`, payload, (progress) => {
      console.log(progress);
      setProgress(progress)
    });
    if (!request.isError) {
      confirmed();
      setAnalysis(undefined);
      setLoading(false);
    }
    setError("Unable to create new analysis, please contact support if the issue persists");
    setLoading(false);
  }

  const validateForm = () => {
    if (!analysis) return;
    setErrors(undefined);
    let errorList: (keyof AnalysisFormType)[] = [];
    let check = checkFormValues(analysis, ["system", "ruleset"]);
    if (!analysis.file)
      check.push("file");
    if (Array.isArray(check))
      errorList = check;
    if (errorList.length > 0) {
      setErrors(errorList);
      return false;
    }
    setErrors(undefined);
    return true;
  }

  const fileChange = (file: FileModel, files: FileList) => {
    setAnalysis({ ...analysis, file: file });
  }

  return (
    <div className="ap-input-form">
      <FormError error={error} />
      <form autoComplete="off">
        <div className="flex flex-col gap-4">
          <div>
            <div className="ap-pattern-form">
              <span className='ap-pattern-form-title'>
                Basic information
              </span>
              <span className='ap-pattern-form-required-indicator'>
                Required Fields
              </span>
            </div>
          </div>
          <div>
            <Combobox
              data={systems.map(m => ({ value: m.systemId, label: m.sapNickname }))}
              onSelect={(value) => setAnalysis({ ...analysis, system: value })}
              value={analysis?.system}
              placeholder="System"
              dropdownRenderMode="portal"
              required
              suffixTemplate={item => {
                return (item.description && <span>{item.description}</span>)
              }}
              error={errors?.includes("system")}
              errorNode={<ValidationError error="System is required" />}
            />
          </div>
          <div>
            <Combobox
              data={ruleSets}
              onSelect={(value) => setAnalysis({ ...analysis, ruleset: value })}
              required
              placeholder="Rule set"
              dropdownRenderMode="portal"
              suffixTemplate={item => {
                return (item.description && <span>{item.description}</span>)
              }}
              error={errors?.includes("ruleset")}
              errorNode={<ValidationError error="Rule set is required" />}
              labelKey="label"
            />
          </div>
          <div>
            <h3>File</h3>
            <Upload
              onChange={fileChange}
              autoUpload={false}
              multiple={false}
              acceptFileType=".zip"
              uploadTitle="Upload extracted data"
              uploadInstruction="You can only upload files that are in PwC Extract format. Otherwise the analysis process will fail."
            />
            {errors?.includes("file") && <ValidationError error="File is required" />}
          </div>
        </div>
      </form>
      <div className="ap-footer flex gap-4">
        <div className="grow">
          <Button onClick={cancel} kind="secondary">Cancel</Button>
        </div>
        <div>
          <Button
            onClick={handleSubmit}
            kind="primary"
            loading={loading}
          >Save</Button>
        </div>
      </div>
      <Modal
        visible={loading}
        header={<>
          <span className="ap-font-medium text-center">Uploading analysis</span>
        </>}
        closable={false}
        modalStyle={{ textAlign: "center", padding: "16px" }}
      >
        <Loading 
          stopPercent={progress}
          loadingType="linear"
        />
      </Modal>
    </div>
  )
}

export default AnalysisForm;
